import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import styles from "./LessonPlanDisplaySidebar.module.css";
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';

/**
 * Separates markdown H2 headings into:
 *  - "Lesson Plan" sub-sections (expandable with sub-items)
 *  - Standalone items: Worksheet, Extensions, Cultural Elements (direct-click, no sub-items)
 * Assessment is excluded.
 */

const worksheetRe = /^worksheet/i;
const extensionsRe = /^extension/i;
const culturalRe = /^cultural/i;
const assessmentRe = /^assessment/i;

function categoriseHeadings(headings) {
  const lessonPlanSections = [];
  const standalone = []; // { label, heading } — click scrolls directly

  headings.forEach(h => {
    if (assessmentRe.test(h)) return;
    if (worksheetRe.test(h)) {
      standalone.push({ label: 'Worksheet', heading: h });
    } else if (extensionsRe.test(h)) {
      standalone.push({ label: 'Extensions', heading: h });
    } else if (culturalRe.test(h)) {
      standalone.push({ label: 'Cultural Elements', heading: h });
    } else {
      lessonPlanSections.push(h);
    }
  });

  return { lessonPlanSections, standalone };
}

function LessonPlanDisplaySidebar({ data, onFieldClick, selectedField, currentView = 'full', lessonData = {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();

  const headings = useMemo(() =>
    (data.match(/^##\s.+$/gm) || []).map(h =>
      h.replace(/^#{2,}\s/, '').trim()
    ).filter(h => !/^assessment$/i.test(h)),
    [data]
  );

  const { lessonPlanSections, standalone } = useMemo(() => categoriseHeadings(headings), [headings]);

  // Lesson Plan group is always expanded (it's the only expandable group)
  const [lessonPlanOpen, setLessonPlanOpen] = useState(true);

  const handleSectionClick = (heading) => {
    if (onFieldClick) onFieldClick(heading);

    const id = heading.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (currentView === 'assessment') {
      const searchParams = new URLSearchParams(location.search);
      const lessonId = searchParams.get('id') || lessonData.id;
      if (lessonId) {
        if (location.pathname === '/view-assessment') {
          navigate(`/view-lesson-plan?id=${lessonId}`, { state: lessonData });
        } else {
          navigate(`/display-lesson-plan?id=${lessonId}`, { state: lessonData });
        }
      }
    }
  };

  return (
    <div className={styles.sidebarContainer}>
      <div className={styles.topSection}>
        {/* Title */}
        <div className={styles.lessonEditorHeader}>
          <div className={styles.lessonEditorTitle}>{t('lesson_editor', language)}</div>
        </div>

        <div className={styles.accordionWrapper}>
          {/* ── Lesson Plan: expandable with sub-sections ── */}
          {lessonPlanSections.length > 0 && (
            lessonPlanOpen ? (
              <div
                className={styles.groupActive}
                onClick={() => setLessonPlanOpen(false)}
              >
                <div className={styles.groupTitle}>Lesson Plan</div>
                <div className={styles.groupSections} onClick={(e) => e.stopPropagation()}>
                  {lessonPlanSections.map((heading, idx) => {
                    const isSectionActive = currentView === 'full' && selectedField === heading;
                    return (
                      <div
                        key={`${heading}-${idx}`}
                        className={`${styles.sectionItem} ${isSectionActive ? styles.sectionItemActive : ''}`}
                        onClick={() => handleSectionClick(heading)}
                      >
                        {heading}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div
                className={styles.groupInactive}
                onClick={() => setLessonPlanOpen(true)}
              >
                <div className={styles.groupTitleInactive}>Lesson Plan</div>
              </div>
            )
          )}

          {/* ── Standalone items: click scrolls directly, no expand ── */}
          {standalone.map(({ label, heading }, idx) => (
            <div
              key={`${label}-${idx}`}
              className={styles.groupInactive}
              onClick={() => handleSectionClick(heading)}
            >
              <div className={styles.groupTitleInactive}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default LessonPlanDisplaySidebar;
